import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Instruction, useInstructions } from "@/hooks/useInstructionQueries";
import { handleApiError } from "@/hooks/useApiQueries";
import { useBreadcrumb } from "@/shared/hooks/useBreadcrumb";
import {
  Button,
  ErrorDisplay,
  Loader,
  NoData,
  PageTitle,
} from "@/shared/components";

type InstructionTableRow = Instruction & {
  title: string;
  videoUrl: string;
};

export default function Instructions() {
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumb();

  const { data: instructions, isLoading, error, isError } = useInstructions();

  useEffect(() => {
    setBreadcrumbs([
      { label: t("breadcrumbs.instructions"), path: "/instructions" },
    ]);
  }, [setBreadcrumbs, t]);

  const language = i18n.language?.split("-")[0] || "lt";

  const userManualFileName = "LM-IDAIS-NV-ISORINIS_v2.00.pdf";

  const rows = useMemo<InstructionTableRow[]>(() => {
    return [...(instructions ?? [])]
      .filter(instruction => instruction.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(instruction => {
        const translation =
          instruction.translations.find(item => item.language === language) ??
          instruction.translations.find(item => item.language === "lt") ??
          instruction.translations.find(item => item.language === "en") ??
          instruction.translations[0];

        return {
          ...instruction,
          title: translation?.title ?? instruction.code,
          videoUrl: translation?.videoUrl ?? "",
        };
      });
  }, [instructions, language]);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorDisplay message={handleApiError(error, t)} />;
  }

  if (rows.length === 0) {
    return <NoData message={t("instructionsPage.noData")} />;
  }

  return (
    <div>
      <div className="flex w-full justify-between">
        <PageTitle>{t("instructionsPage.userManual")}</PageTitle>
        <a href={userManualFileName} download>
          <Button variant="primary" className="mb-5">
            {t("instructionsPage.actions.downloadUserManual")}
          </Button>
        </a>
      </div>

      <PageTitle>{t("instructionsPage.title")}</PageTitle>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left border-collapse">
          {/* <thead>
            <tr className="border-b">
              <th className="py-2 pr-4 font-medium">
                {t("instructionsPage.fields.title")}
              </th>
              <th className="py-2 font-medium">
                {t("instructionsPage.fields.video")}
              </th>
            </tr>
          </thead> */}
          <tbody>
            {rows.map(instruction => (
              <tr key={instruction.code}>
                <td className="py-3 pr-4 align-top">{instruction.title}</td>
                <td className="py-3 align-top">
                  <div className="flex justify-end">
                    {instruction.videoUrl ? (
                      <Button
                        variant="primary"
                        onClick={() =>
                          window.open(instruction.videoUrl, "_blank")
                        }
                      >
                        {t("instructionsPage.actions.openVideo")}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
